/**
 * Hooks de lectura del dominio Estadísticas avanzadas (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api` (cookie httpOnly +
 * `X-Sede-Id`). Cada métrica se cachea por sede activa (y por período las series),
 * de modo que al cambiar de sede o período se pide la combinación correspondiente.
 *
 * Todos los hooks aceptan `enabled`: la pantalla solo activa los analytics con el
 * permiso correspondiente (mismo patrón que `useEffectiveness` del dashboard), de
 * modo que **no se pide** un dato que el actor no debe ver (los pedidos de láminas
 * exigen `view_pedidos_laminas`, distinto del resto).
 */
import { useQuery } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import {
  fetchDailyOrders,
  fetchEffectiveness,
  fetchOrdersChart,
  fetchOrdersTiming,
  fetchPedidosLaminas,
  fetchRevenueChart,
  fetchSalesBySeller,
} from "../api";
import type { OrdersPeriod, RevenuePeriod, SellerPeriod } from "../schemas";
import type {
  DailyOrdersStats,
  EffectivenessStats,
  OrdersChartStats,
  OrdersTimingStats,
  PedidosLaminasStats,
  RevenueChartStats,
  SalesBySellerStats,
} from "../types";
import {
  dailyOrdersQueryKey,
  effectivenessQueryKey,
  ordersChartQueryKey,
  ordersTimingQueryKey,
  pedidosLaminasQueryKey,
  revenueChartQueryKey,
  salesBySellerQueryKey,
} from "./queryKeys";

/** Margen corto compartido: evita refetch agresivo sin volver los datos rancios. */
const STALE_TIME = 30_000;

/** Efectividad de entregas de la sede activa. */
export function useEffectiveness(enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<EffectivenessStats>({
    queryKey: effectivenessQueryKey(sedeId),
    queryFn: fetchEffectiveness,
    enabled,
    staleTime: STALE_TIME,
  });
}

/** Órdenes por día (últimos 30 con actividad) de la sede activa. */
export function useDailyOrders(enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<DailyOrdersStats>({
    queryKey: dailyOrdersQueryKey(sedeId),
    queryFn: fetchDailyOrders,
    enabled,
    staleTime: STALE_TIME,
  });
}

/** Tiempos creación->entrega y detalle por orden de la sede activa. */
export function useOrdersTiming(enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<OrdersTimingStats>({
    queryKey: ordersTimingQueryKey(sedeId),
    queryFn: fetchOrdersTiming,
    enabled,
    staleTime: STALE_TIME,
  });
}

/** Serie temporal de cantidad de órdenes para el período dado. */
export function useOrdersChart(period: OrdersPeriod, enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<OrdersChartStats>({
    queryKey: ordersChartQueryKey(sedeId, period),
    queryFn: () => fetchOrdersChart(period),
    enabled,
    staleTime: STALE_TIME,
  });
}

/** Serie temporal de ingresos para el período dado. */
export function useRevenueChart(period: RevenuePeriod, enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<RevenueChartStats>({
    queryKey: revenueChartQueryKey(sedeId, period),
    queryFn: () => fetchRevenueChart(period),
    enabled,
    staleTime: STALE_TIME,
  });
}

/** Ventas por vendedor/atendido para el período dado. */
export function useSalesBySeller(period: SellerPeriod, enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<SalesBySellerStats>({
    queryKey: salesBySellerQueryKey(sedeId, period),
    queryFn: () => fetchSalesBySeller(period),
    enabled,
    staleTime: STALE_TIME,
  });
}

/**
 * Pedidos de láminas recibidos para el período dado. El endpoint exige
 * `view_pedidos_laminas` (distinto del resto de analytics): la pantalla solo
 * habilita este hook con ese permiso, para no recibir un 403 por un dato del que el
 * resto de la pantalla no depende.
 */
export function usePedidosLaminas(period: OrdersPeriod, enabled = true) {
  const sedeId = getActiveSedeId();
  return useQuery<PedidosLaminasStats>({
    queryKey: pedidosLaminasQueryKey(sedeId, period),
    queryFn: () => fetchPedidosLaminas(period),
    enabled,
    staleTime: STALE_TIME,
  });
}
